import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { X } from "react-bootstrap-icons";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { isNil, isEmpty } from "lodash";
import { isMobile } from "react-device-detect";

import AppButton from "../../../../components/common/buttons/app-button";
import CustomSvg from "../../../../components/common/custom-svg/custom-svg";
import ResponsiveWrapper from "../../../../components/common/ResponsiveWrapper";
import AgentCard from "../../../../components/common/AgentCard";
import ArchiveIcon from "../../../../assets/icons-v2/archive.png";
import StatsIcon from "../../../../assets/icons-v2/stats.png";
import GamesIcon from "../../../../assets/icons-v2/games.png";
import useGAEvent from "../../../../hooks/useGAEvent";
import useMixpanelEvent from "../../../../hooks/useMixpanelEvent";
import useLocalStorage from "../../../../hooks/useLocalStorage";
import { useLiveVariant } from "../../../../context/LiveVariantContext";
import { useUser } from "../../../../context/UserContext";
import { getSalesData } from "../../../../services/PropertyService";
import "./PuzzleDailyScoreModal.scss";

/**
 * A modal that displays the user's daily score and provides feedback based on the score.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {boolean} props.show - Controls the visibility of the modal.
 * @param {number} props.score - The user's score.
 * @param {number} props.streak - The user's current streak.
 * @param {Object} props.version - The version of live-variant.
 * @param {Object} props.property - The property object.
 * @param {Function} props.handleClose - The function to close the modal.
 * @param {Function} props.handleShare - The function to handle sharing.
 * @param {JSX.Element} props.puzzleResult - The component that displays the live variant puzzle result
 * @returns {JSX.Element}
 */
export default function PuzzleDailyScoreModal({
    show,
    score,
    streak,
    version,
    property,
    handleClose,
    handleShare,
    puzzleResult,
}) {
    const { userState, setUserState } = useUser();
    const { sendEvent } = useGAEvent();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { liveVariantState, setLiveVariantState } = useLiveVariant();
    const [visitorStatus] = useLocalStorage("visitor-status");
    const [isExpanded, setIsExpanded] = useState(false);
    const [agentData, setAgentData] = useState(null);

    const { data: salesData } = useQuery({
        queryKey: ["current_property_sales_data", property.id],
        queryFn: () => getSalesData(property.id),
        enabled: !isNil(property.id) && !isEmpty(property.id),
    });

    useEffect(() => {
        const listingAgent = salesData?.sales_data?.listingAgent;

        if (listingAgent) {
            const transformedAgentData = {
                name: `${listingAgent.first_name} ${listingAgent.last_name}`,
                company: listingAgent.brokerage || "",
                profilePicture: listingAgent.img || null,
                profileUrl: listingAgent.link || null,
                cta: listingAgent.cta || null,
                mlsId: listingAgent.mls_id || null,
                socialLinks: {
                    instagram: listingAgent.instagram,
                    tiktok: listingAgent.tiktok,
                    twitter: listingAgent.x_twitter,
                    facebook: listingAgent.facebook,
                    youtube: listingAgent.youtube,
                },
            };
            setAgentData(transformedAgentData);
        } else {
            setAgentData(null);
        }
    }, [salesData]);

    /**
     * Handles the closing of the live-variant daily score modal.
     */
    const handleCloseModal = () => {
        sendEvent("daily_score_close", {
            userScore: score,
            liveVariantVersion: version?.gaVersion,
            visitorStatus: visitorStatus || "new",
        });
        handleClose();
    };

    const handleShareScore = async (event) => {
        sendEvent("puzzle_daily_score_share", {
            userScore: score,
            liveVariantVersion: version?.gaVersion,
            visitorStatus: visitorStatus || "new",
        });

        // Send Mixpanel Share Score event
        sendMixpanelEvent("Share Score", {
            isAuthenticated: !!userState?.id,
            visitorStatus: visitorStatus || "new-guest",
            pageName: "Puzzle Daily Score Modal",
            propertyUrl: `${window.location.origin}/puzzle/${liveVariantState?.shortLink}`,
            email: userState?.email_address || null,
            nickname: userState?.nickname || null,
            location: "Puzzle Score Only",
            userScore: score,
        });

        // Call handleShare without shareUrl for "Share Score" button
        await handleShare(event, false, false, false);

        // Expand drawer after 3 seconds if not already expanded
        if (!isExpanded) {
            setTimeout(() => {
                setIsExpanded(true);
            }, 3000);
        }
    };

    const handleShareWithLink = async (event) => {
        sendEvent("puzzle_daily_score_share_with_link", {
            userScore: score,
            liveVariantVersion: version?.gaVersion,
            visitorStatus: visitorStatus || "new",
        });

        sendMixpanelEvent("Share Score", {
            isAuthenticated: !!userState?.id,
            visitorStatus: visitorStatus || "new-guest",
            pageName: "Puzzle Daily Score Modal",
            propertyUrl: `${window.location.origin}/puzzle/${liveVariantState?.shortLink}`,
            email: userState?.email_address || null,
            nickname: userState?.nickname || null,
            location: "Puzzle With Link",
            userScore: score,
        });

        // Call handleShare with shareUrl for "Share with link" button
        await handleShare(event, false, true, false);

        // Expand drawer after 3 seconds if not already expanded
        if (!isExpanded) {
            setTimeout(() => {
                setIsExpanded(true);
            }, 3000);
        }
    };

    const handleExploreArchive = () => {
        setLiveVariantState({ ...liveVariantState, show_archive_select_modal: true });
        handleClose();
    };

    const handleSeeStats = () => {
        sendEvent("daily_score_see_stats", {
            userScore: score,
            liveVariantVersion: version?.gaVersion,
            visitorStatus: visitorStatus || "new",
        });
        setUserState({ ...userState, leaderboardSelectedVersion: "puzzle" });
        window.location.href = "/leaderboard";
    };

    const handleMoreGames = () => {
        window.location.href = "/";
    };

    useEffect(() => {
        sendEvent("daily_score_view", {
            userScore: score,
            liveVariantVersion: version?.gaVersion,
            visitorStatus,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset expansion state when modal is shown
    useEffect(() => {
        if (show) {
            setIsExpanded(false);
        }
    }, [show]);

    return (
        <ResponsiveWrapper
            centered
            data-cy="live-variant-daily-score-modal"
            keyboard={false}
            show={show}
            onHide={handleCloseModal}
            className="puzzle-daily-score-modal"
            isExpandable={true}
            isExpanded={isExpanded}
            drawerHeight="60vh"
            expandedHeight={property?.featured_agent ? "95vh" : "80vh"}
            showHandle={true}
            onExpand={() => setIsExpanded(true)}
        >
            {/* X button - only show in expanded state */}
            {isExpanded && (
                <div className="tw-flex tw-justify-end tw-px-2">
                    <X size={30} onClick={handleCloseModal} />
                </div>
            )}

            <div className="tw-px-4 tw-pb-6 tw-overflow-y-auto tw-max-h-[87vh]">
                <div className="tw-text-center tw-flex tw-flex-col tw-items-center tw-gap-4">
                    {/* Swipe up indicator - only show when not expanded */}
                    {!isExpanded && (
                        <motion.div
                            className="tw-text-center tw-mt-4 tw-cursor-pointer"
                            onClick={() => setIsExpanded(true)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <p className="tw-text-[#605E5E] !tw-text-[1.8vh] md:!tw-text-[12px] !tw-font-noto tw-mb-2">
                                {isMobile ? "Swipe up for more" : "Click here for more"}
                            </p>
                            <motion.div
                                animate={{ y: [0, -5, 0] }}
                                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                className="tw-flex tw-justify-center"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M7 14L12 9L17 14"
                                        stroke="#605E5E"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </motion.div>
                        </motion.div>
                    )}

                    {/* Share Section */}
                    <div className="tw-text-center tw-flex tw-flex-col tw-items-center">
                        <p className="!tw-font-merriweather !tw-font-bold !tw-text-[3vh] tw-mb-0">
                            Send to a friend
                        </p>
                        <p className="tw-text-[#27273B] !tw-text-[2vh] md:!tw-text-[12px] !tw-font-noto">
                            Know someone who needs to see this listing?
                        </p>
                        <div className="tw-bg-[#2E2E2E] tw-py-4 tw-px-8 tw-rounded-[10px] tw-mb-2">
                            {puzzleResult}
                        </div>
                        <AppButton
                            variant="solid"
                            theme="gold"
                            className="modal-button !tw-flex !tw-items-center !tw-justify-center !tw-w-[200px] !tw-mt-0 !tw-h-[6.6vh] md:!tw-h-[52px]"
                            onClick={handleShareScore}
                        >
                            Share Score
                        </AppButton>
                        <button
                            className="tw-text-[#6C26C4] !tw-text-[2vh] md:!tw-text-[14px] tw-pb-1 !tw-font-noto tw-bg-transparent tw-border-none tw-cursor-pointer"
                            onClick={handleShareWithLink}
                        >
                            Share with link
                        </button>
                    </div>
                </div>

                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 20 }}
                            transition={{ duration: 0.3 }}
                            className="tw-mt-1"
                        >
                            <hr className="tw-w-full tw-border-t tw-border-gray-300 tw-opacity-60 tw-my-1" />

                            {agentData && (
                                <AgentCard agent={agentData} clickLocation="daily_score_modal" />
                            )}

                            {/* Streak Section - only visible in expanded state */}
                            <div className="tw-font-noto tw-text-center">
                                <p className="!tw-text-[2.4vh] md:!tw-text-[14px] tw-text-[#605E5E] tw-font-bold tw-mb-0 tw-mt-1">
                                    Your Current Solve Streak:
                                </p>
                                <div className="tw-text-[4.8vh] md:tw-text-[28px] tw-text-[#6C26C4] tw-font-bold tw-items-center tw-flex tw-justify-center">
                                    {streak}
                                </div>
                            </div>

                            <div className="tw-bg-white tw-rounded-xl tw-py-2 tw-px-5 tw-shadow-md">
                                <div className="tw-flex tw-justify-between tw-items-center">
                                    <div
                                        className="tw-flex tw-flex-col tw-items-center tw-cursor-pointer"
                                        onClick={handleExploreArchive}
                                    >
                                        <CustomSvg src={ArchiveIcon} size={{ height: 24, width: 24 }} />
                                        <span className="tw-text-xs tw-text-gray-600 tw-mt-1">Archive</span>
                                    </div>
                                    <div
                                        className="tw-flex tw-flex-col tw-items-center tw-cursor-pointer"
                                        onClick={handleSeeStats}
                                    >
                                        <CustomSvg src={StatsIcon} size={{ height: 24, width: 24 }} />
                                        <span className="tw-text-xs tw-text-gray-600 tw-mt-1">Stats</span>
                                    </div>
                                    <div
                                        className="tw-flex tw-flex-col tw-items-center tw-cursor-pointer"
                                        onClick={handleMoreGames}
                                    >
                                        <CustomSvg src={GamesIcon} size={{ height: 24, width: 24 }} />
                                        <span className="tw-text-xs tw-text-gray-600 tw-mt-1">More</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </ResponsiveWrapper>
    );
}

PuzzleDailyScoreModal.propTypes = {
    show: PropTypes.bool.isRequired,
    score: PropTypes.number.isRequired,
    streak: PropTypes.number.isRequired,
    version: PropTypes.object.isRequired,
    property: PropTypes.object.isRequired,
    handleClose: PropTypes.func.isRequired,
    handleShare: PropTypes.func.isRequired,
    puzzleResult: PropTypes.element,
};
